import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEqual } from "lodash";
import moment from "moment";
import { DatePicker, Popconfirm } from "antd";
import ReactTable from "react-table";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomerInvoicingStatuses, { CustomerInvoicingStatusKeys } from "data/customer-invoicing-statuses";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import exportFromBlob from "helpers/export-from-blob";
import { openCustomModal, closeCustomModal } from "components/custom/modals";
import {
    patchCustomerChargeBillingHours,
    deleteCustomerInvoicingInvoice,
    fetchCustomerPdfInvoice,
    fetchCustomerPdfSummary,
    fetchCustomerCsvSummary,
    fetchCustomerExcelSummary,
    customerSendInvoice,
} from "api/menu/invoicing/actions";

const PdfViewerModal = React.lazy(() =>
    import("components/menu/invoicing/merchandiser/pdf-viewer-modal"));
const EditInvoiceModal = React.lazy(() =>
    import("components/menu/invoicing/customer/edit-invoice-modal"));

class ListInvoicesInvoices extends Component {
    dateFormat = "YYYY-MM-DD";

    constructor(props) {
        super(props);
        this.handleViewInvoicePdf = this.handleViewInvoicePdf.bind(this);
        this.handleSendInvoiceAndSummary = this.handleSendInvoiceAndSummary.bind(this);
    }

    shouldComponentUpdate(nextProps) {
        const updatedDataLength = nextProps.data.filter((item, index) => {
            if (!this.props.data[index]) {
                return true;
            } else if (!isEqual(item, this.props.data[index])) {
                return true;
            }
            return false;
        });
        if (updatedDataLength) {
            return true;
        } else if (nextProps.isAdminView !== this.props.isAdminView) {
            return true;
        }
        return false;
    }

    handleViewInvoicePdf = (invoiceId) => {
        this.props.fetchCustomerPdfInvoice(invoiceId, (pdfData) => {
            const modalObj = {
                id: "view-customer-invoice",
                modalSettings: {
                    size: "lg",
                    style: {
                        top: 20,
                        width: "calc(100% - 40px)"
                    },
                },
                header: "View Invoice",
                children: (
                    <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                        <PdfViewerModal
                            pdfData={pdfData}
                            name="customer-invoice.pdf"
                        />
                    </React.Suspense>
                ),
                handleClose: () => this.props.closeCustomModal(modalObj)
            };
            this.props.openCustomModal(modalObj);
        });
    };

    handleViewSummaryPdf = (invoiceId) => {
        this.props.fetchCustomerPdfSummary(invoiceId, (pdfData) => {
            this.PDFModal = React.createRef();
            const modalObj = {
                id: "view-customer-summary",
                modalSettings: {
                    size: "lg",
                    style: {
                        top: 20,
                        width: "calc(100% - 40px)"
                    },
                },
                header: (
                    <div className="d-flex justify-content-between">
                        <div className="ant-modal-title" id="rcDialogTitle0">View Summary</div>
                        <div className="text-right mr-5">
                            <button
                                className="btn btn-success"
                                onClick={() => this.PDFModal.current.handleSaveFile()}
                            >
                                Export PDF
                            </button>
                            <button
                                className="btn btn-success ml-2"
                                onClick={() => {
                                    this.props.fetchCustomerExcelSummary(invoiceId, (response) => {
                                        exportFromBlob(response, this.props.csvName || "CustomerSummary.xlsx");
                                    });
                                }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                ),
                children: (
                    <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                        <PdfViewerModal
                            ref={this.PDFModal}
                            invoiceId={invoiceId}
                            pdfData={pdfData}
                            customHeader={true}
                            name="customer-summary.pdf"
                            csvName="customer-summary.csv"
                        />
                    </React.Suspense>
                ),
                handleClose: () => this.props.closeCustomModal(modalObj)
            };
            this.props.openCustomModal(modalObj);
        });
    };

    handleEditModal = (invoiceId) => {
        const modalObj = {
            id: "edit-invoice-modal",
            modalSettings: {
                size: "lg",
                style: {
                    top: 20,
                    width: "calc(100% - 40px)"
                },
            },
            header: "Edit Invoice",
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <EditInvoiceModal
                        invoiceId={invoiceId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        handleLoad={() => this.props.handleLoad()}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleSendInvoiceAndSummary = (invoiceId) => {
        this.props.customerSendInvoice(invoiceId);
    };

    getColumns = () => {
        const columns = [];

        // address1: "333 North Rivermede Road"
        // address2: "Unit 1 "
        // attn: ""
        // batchNum: 2841
        // city: "Concord"
        // clientAddress1: ""
        // clientAddress2: ""
        // clientCity: ""
        // clientId: 116
        // clientPostal: ""
        // clientProvince: ""
        // companyName: "Counter Intelligence Merchandising Inc."
        // conversionRate: 1
        // currency: "CAD"
        // fax: "(905) 738-2313"
        // gstNumber: "887245298"
        // gstPercent: 5
        // invoiceId: 21522
        // invoicePeriod: "From 20 May 2020 to 27 May 2020"
        // phone: "(905) 738-0048"
        // postalCode: "L4K 3N7"
        // province: "ON"
        // totalCurrency: 0

        columns.push({
            Header: "Invoice Num",
            accessor: "invoiceNum"
        });
        columns.push({
            id: "invoice-date",
            Header: "Invoice Date",
            accessor: original => moment(new Date(original.invoiceDate)).format(this.dateFormat),
            Filter: ({ filter, onChange }) => (
                <DatePicker
                    Format={this.dateFormat}
                    value={filter ? filter.value : null}
                    onChange={date => onChange(date)}
                    placeholder=""
                />
            )
        });
        columns.push({
            Header: "Client Name",
            accessor: "clientName"
        });
        columns.push({
            Header: "Sub Total",
            accessor: "subTotal"
        });
        columns.push({
            Header: "GST",
            accessor: "gst"
        });
        columns.push({
            Header: "Total",
            accessor: "total"
        });
        columns.push({
            id: "status",
            Header: "Status",
            accessor: "status",
            Cell: ({ original }) => (
                <div
                    className="text-center"
                    style={{
                        color: original.status
                            ? CustomerInvoicingStatuses[
                                CustomerInvoicingStatusKeys[original.status]].color
                            : ""
                    }}
                >
                    {original.status > -1
                        ? CustomerInvoicingStatuses[
                            CustomerInvoicingStatusKeys[original.status]].title
                        : ""}
                </div>
            ),
            Filter: ({ filter, onChange }) => (
                <select
                    style={{ width: "100%", height: "100%" }}
                    onChange={ev => onChange(ev.target.value)}
                    value={filter && typeof filter.value !== "undefined" ? filter.value : ""}
                >
                    <option value="">All</option>
                    {Object.keys(CustomerInvoicingStatuses).map(key => (
                        <option
                            key={key}
                            value={parseInt(CustomerInvoicingStatuses[key].value, 10)}
                        >
                            {CustomerInvoicingStatuses[key].title}
                        </option>
                    ))}
                </select>
            ),
            filterMethod: (filter, row) => {
                if (typeof filter.value === "undefined" || filter.value === "") {
                    return true;
                }
                filter.value = parseInt(filter.value, 10);
                return filter.value === row.status;
            }
        });
        columns.push({
            id: "actions",
            Header: "Actions",
            style: {
                overflow: "unset"
            },
            Cell: ({ original }) => (
                <div>
                    <ButtonGroup className="mr-3">
                            <Button size="sm" onClick={() => this.handleEditModal(original.invoiceId)}>
                                <FontAwesomeIcon icon="pen" title="Update" />
                            </Button>
                        {original.status === CustomerInvoicingStatuses.Preliminary.value ?
                            <Popconfirm
                                title={"Delete Invoice?"}
                                onConfirm={() =>
                                    this.props.deleteCustomerInvoicingInvoice(original.invoiceId)}
                                placement="bottomRight"
                                okText="Yes"
                                cancelText="Cancel"
                            >
                                <Button variant="danger" size="sm">
                                    <FontAwesomeIcon icon="times"/>
                                </Button>
                            </Popconfirm>
                            : null}

                        <Dropdown>
                            <Dropdown.Toggle variant="info" size="sm" />

                            <Dropdown.Menu
                                style={{
                                    top: 30,
                                    left: 0,
                                    zIndex: 1,
                                    transform: "none"
                                }}
                            >
                                <Dropdown.Item onClick={() =>
                                    this.handleViewInvoicePdf(original.invoiceId)}>
                                    View Invoice
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() =>
                                    this.handleViewSummaryPdf(original.invoiceId)}>
                                    View Summary
                                </Dropdown.Item>
                                {original.status !== CustomerInvoicingStatuses.Preliminary.value ?
                                    <Dropdown.Item onClick={() =>
                                        this.handleSendInvoiceAndSummary(original.invoiceId)}>
                                        Send Invoice and Summary
                                    </Dropdown.Item>
                                    : null}
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                    {/*
                    <ButtonGroup className="mr-3">
                        {original.status === CustomerInvoicingStatuses.Preliminary.value ?
                            <Button size="sm" onClick={() => this.handleEditModal(original.invoiceId)}>
                                <FontAwesomeIcon icon="pen" title="Update" />
                            </Button>
                        : null}
                        {original.status === CustomerInvoicingStatuses.Preliminary.value ?
                            <Popconfirm
                                title={"Delete Invoice?"}
                                onConfirm={() =>
                                    this.props.deleteCustomerInvoicingInvoice(original.invoiceId)}
                                placement="bottomRight"
                                okText="Yes"
                                cancelText="Cancel"
                            >
                                <Button variant="danger" size="sm">
                                    <FontAwesomeIcon icon="times"/>
                                </Button>
                            </Popconfirm>
                        : null}
                    </ButtonGroup>
                    <ButtonGroup>
                        <Button variant="primary" size="sm" onClick={() => this.handleViewInvoicePdf(original.invoiceId)}>
                            View Invoice
                        </Button>
                        <Button variant="warning" size="sm" onClick={() => this.handleViewSummaryPdf(original.invoiceId)}>
                            View Summary
                        </Button>

                        {original.status !== CustomerInvoicingStatuses.Preliminary.value ?
                        <Button
                            variant="info"
                            size="sm"
                            onClick={() => this.handleSendInvoiceAndSummary(original.invoiceId)}
                        >
                            Send Invoice and Summary
                        </Button>
                        : null}
                    </ButtonGroup>
                    */}
                </div>
            ),
            Filter: () => false,
            width: 220
        });

        return columns.map(value => {
            if (value.headerClassName) {
                value.headerClassName = `${value.headerClassName} text-left`;
            } else {
                value.headerClassName = "text-left";
            }
            return value;
        });
    };

    render() {
        const { data } = this.props;

        return (
            <div>
                <ReactTable
                    data={data}
                    columns={this.getColumns()}
                    headerClassName="table-header"
                    showPagination={true}
                    defaultPageSize={20}
                    pageSizeOptions={[20, 50, 100, 500, 1000]}
                    resizable
                    className="-striped -highlight"
                    filterable
                    defaultSorted={[{
                        id: "completedDate",
                        desc: false
                    }]}
                    defaultFilterMethod={filterCaseInsensitive}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.row) {
                            return {
                                style: {
                                    background: rowInfo.row.amount < 0 ? "#ffc0cb" : "",
                                }
                            };
                        }
                        return {};
                    }}
                    style={{
                        height: "calc(100vh - 240px)",
                    }}
                />
            </div>
        );
    }
}

ListInvoicesInvoices.propTypes = {
    data: PropTypes.array.isRequired,
    isAdminView: PropTypes.bool.isRequired,
};

const mapStateToDispatch = dispatch => ({
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

    patchCustomerChargeBillingHours: bindActionCreators(patchCustomerChargeBillingHours, dispatch),
    deleteCustomerInvoicingInvoice: bindActionCreators(deleteCustomerInvoicingInvoice, dispatch),
    fetchCustomerPdfInvoice: bindActionCreators(fetchCustomerPdfInvoice, dispatch),
    fetchCustomerPdfSummary: bindActionCreators(fetchCustomerPdfSummary, dispatch),
    fetchCustomerCsvSummary: bindActionCreators(fetchCustomerCsvSummary, dispatch),
    fetchCustomerExcelSummary: bindActionCreators(fetchCustomerExcelSummary, dispatch),
    customerSendInvoice: bindActionCreators(customerSendInvoice, dispatch),
});

export default connect(
    null,
    mapStateToDispatch
)(ListInvoicesInvoices);
